// src/pages/league-dashboard/TeamTransactions.js
// Phase 8: Team Transactions Page with Cash Ledger Tab
import React, { useState, useEffect } from 'react';
import { DollarSign, ArrowRightLeft, Calendar, Users, ChevronDown, X, AlertTriangle, Undo2 } from 'lucide-react';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import { dynastyTheme } from '../../services/colorService';
import { DynastyTable } from '../../services/tableService';
import { leaguesAPI } from '../../services/apiService';
import PlayerProfileModal from '../../components/PlayerProfileModal';
import PlayerLink from '../../components/PlayerLink';
import { useCommissioner } from '../../contexts/CommissionerContext';

// 9k: Reverse Transaction confirmation modal. Mirrors the visual pattern of the
// inline ConfirmModal in MinorLeagueDraft.js + 9f.3's PromotionConfirmModal — same
// AlertTriangle header, secondary Cancel autofocus, destructive Confirm button.
// Per Tony's C4, wording emphasizes that reversal is a commissioner action with effects.
const ReverseConfirmModal = ({ description, inFlight, onConfirm, onCancel }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label="Reverse Transaction Confirmation"
    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
  >
    <div
      className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral} max-w-md w-full`}
      style={{
        borderRadius: dynastyTheme.tokens.radius.lg,
        padding: dynastyTheme.tokens.spacing.xl,
      }}
    >
      <div className="text-center" style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
        <div
          className={`flex items-center justify-center mx-auto ${dynastyTheme.classes.bg.warning} ${dynastyTheme.classes.shadow.base}`}
          style={{
            width: '3rem',
            height: '3rem',
            borderRadius: dynastyTheme.tokens.radius.lg,
            marginBottom: dynastyTheme.tokens.spacing.md,
          }}
        >
          <AlertTriangle className={dynastyTheme.classes.text.black} style={{ width: '1.5rem', height: '1.5rem' }} />
        </div>
        <h3 className={`${dynastyTheme.components.heading.h2} ${dynastyTheme.classes.text.white} mb-2`}>
          Reverse this transaction?
        </h3>
      </div>
      <div className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-6`} style={{ lineHeight: '1.5' }}>
        {description && (
          <p style={{ marginBottom: dynastyTheme.tokens.spacing.md }}>
            <span className={dynastyTheme.classes.text.white}>{description}</span>
          </p>
        )}
        <p>
          This will undo the transaction's effects on rosters, salaries, and cash balances.
          The reversal is logged and visible in the transaction history.
        </p>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          autoFocus
          disabled={inFlight}
          onClick={onCancel}
          className={`flex-1 ${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} justify-center`}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={inFlight}
          onClick={onConfirm}
          className={`flex-1 ${dynastyTheme.utils.getComponent('button', 'ghost', 'md')} ${dynastyTheme.classes.text.error} hover:bg-red-500/20 justify-center`}
        >
          {inFlight ? 'Reversing…' : 'Reverse'}
        </button>
      </div>
    </div>
  </div>
);

const TeamTransactions = ({ leagueId, league, user, userTeam, teams = [] }) => {
  // 9k: read isCommissioner so the Actions column can offer Reverse Transaction
  // for non-trade rows. Backend DELETE /transactions/{id} enforces commissioner
  // authorization regardless; this is UI-side filtering for clarity.
  const { isCommissioner } = useCommissioner();

  const [selectedTeamId, setSelectedTeamId] = useState(userTeam?.team_id || null);
  const [selectedTeamName, setSelectedTeamName] = useState(userTeam?.team_name || 'My Team');
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' or 'ledger'
  const [transactions, setTransactions] = useState([]);
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [availableTeams, setAvailableTeams] = useState(teams);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [loadingTrade, setLoadingTrade] = useState(false);
  // 9k: pending reverse confirmation — { transactionId, description } when open, null when idle
  const [pendingReverse, setPendingReverse] = useState(null);
  const [reverseInFlight, setReverseInFlight] = useState(false);

  // Sync availableTeams from teams prop
  useEffect(() => {
    if (teams && teams.length > 0) {
      setAvailableTeams(teams);
      if (!selectedTeamId) {
        const myTeam = teams.find(t => t.team_id === userTeam?.team_id);
        const defaultTeam = myTeam || teams[0];
        if (defaultTeam) {
          setSelectedTeamId(defaultTeam.team_id);
          setSelectedTeamName(defaultTeam.team_name);
        }
      }
    }
  }, [teams]);

  // Load data when team changes
  useEffect(() => {
    if (selectedTeamId) {
      if (activeTab === 'transactions') {
        loadTransactions();
      } else {
        loadLedger();
      }
    }
  }, [selectedTeamId, activeTab]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await leaguesAPI.getTeamTransactions(leagueId, selectedTeamId);
      if (response.success) {
        setTransactions(response.transactions || []);
        if (response.team_name) {
          setSelectedTeamName(response.team_name);
        }
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await leaguesAPI.getTeamCashLedger(leagueId, selectedTeamId);
      if (response.success) {
        setLedgerData({
          current_balance: response.current_balance,
          ledger_entries: response.ledger_entries || []
        });
        if (response.team_id) {
          // Team name might be in response
        }
      }
    } catch (err) {
      console.error('Error loading ledger:', err);
      setError('Failed to load cash ledger');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = (teamId) => {
    setSelectedTeamId(teamId);
    const selectedTeam = availableTeams.find(t => t.team_id === teamId);
    if (selectedTeam) {
      setSelectedTeamName(selectedTeam.team_name);
    }
  };

  const handlePlayerClick = (playerId) => {
    setSelectedPlayerId(playerId);
    setIsPlayerModalOpen(true);
  };

  // 9k: open the confirmation modal for a non-trade transaction reverse.
  const openReverseConfirm = (transactionId, description) => {
    setPendingReverse({ transactionId, description });
  };

  // 9k: execute reverse after confirmation. Backend enforces commissioner authorization
  // (DELETE /api/leagues/{id}/transactions/{tid}); UI also gates visibility on isCommissioner.
  const handleReverseConfirm = async () => {
    if (!pendingReverse) return;
    const { transactionId } = pendingReverse;
    try {
      setReverseInFlight(true);
      await leaguesAPI.reverseTransaction(leagueId, transactionId);
      setPendingReverse(null);
      // Refresh the list so the row flips to is_reversed=true badge
      await loadTransactions();
    } catch (err) {
      console.error('Error reversing transaction:', err);
      setError(`Reverse failed: ${err?.message || 'Unknown error'}`);
      setPendingReverse(null);
    } finally {
      setReverseInFlight(false);
    }
  };

  const handleReverseCancel = () => {
    setPendingReverse(null);
  };

  const handleViewTrade = async (tradeId) => {
    if (!tradeId) return;
    
    try {
      setLoadingTrade(true);
      // Fetch trade history and find the specific trade
      const response = await leaguesAPI.getTradeHistory(leagueId, { limit: 1000 });
      if (response.success && response.trades) {
        const trade = response.trades.find(t => t.trade_id === tradeId);
        if (trade) {
          setSelectedTrade(trade);
        } else {
          setError('Trade not found');
        }
      }
    } catch (err) {
      console.error('Error loading trade details:', err);
      setError('Failed to load trade details');
    } finally {
      setLoadingTrade(false);
    }
  };

  // Prepare teams for dropdown (My Team first, then alphabetically)
  const sortedTeams = React.useMemo(() => {
    if (!availableTeams.length) return [];
    const myTeam = availableTeams.find(t => t.team_id === userTeam?.team_id);
    const otherTeams = availableTeams.filter(t => t.team_id !== userTeam?.team_id);
    otherTeams.sort((a, b) => (a.team_name || '').localeCompare(b.team_name || ''));
    return myTeam ? [myTeam, ...otherTeams] : otherTeams;
  }, [availableTeams, userTeam]);

  // Transactions table columns
  const transactionColumns = [
    {
      key: 'date',
      title: 'Date',
      width: 100,
      sortable: true,
      render: (value) => {
        if (!value) return '-';
        const date = new Date(value);
        return (
          <div className={dynastyTheme.classes.text.neutralLight}>
            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        );
      }
    },
    {
      key: 'transaction_type',
      title: 'Type',
      width: 120,
      sortable: true,
      render: (value) => {
        const typeLabels = {
          'trade': 'Trade',
          'trade_reversal': 'Trade Reversed',
          'add': 'Add',
          'drop': 'Drop',
          'release': 'Release',
          'minor_promotion': 'Promotion',
          'fa_acquisition': 'FA Add',
          'free_agent': 'FA Claim',
          'commissioner_add': 'Commish Add',
          'draft_pick': 'Drafted',
          'autodraft': 'Autodraft',
          'minor_draft_pick': 'Minor Draft',
          // B.11.A: roster_lock label for league_transactions per-rostered-player
          // snapshot rows written at start_season.
          'roster_lock': 'Roster Lock',
          // 'keeper_selection' label REMOVED in B.11.A+B+C — no backend writer
          // creates this transaction_type (verified Q-B11-8 audit).
          'keeper_unsubmit': 'Keeper Unsubmit',
          'roster_move': 'Move'
        };
        return (
          <div className={dynastyTheme.classes.text.white}>
            {typeLabels[value] || value}
          </div>
        );
      }
    },
    {
      key: 'description',
      title: 'Description',
      width: 200,
      sortable: false,
      render: (value) => (
        <div className={dynastyTheme.classes.text.neutralLight}>
          {value || '-'}
        </div>
      )
    },
    {
      key: 'players_received',
      title: 'Players In',
      width: 150,
      sortable: false,
      render: (value, row) => {
        const players = row.players_received || [];
        if (players.length === 0) return <div className={dynastyTheme.classes.text.neutralLighter}>-</div>;
        return (
          <div className="space-y-1">
            {players.map((player, idx) => (
              <span key={idx} className="text-sm block">
                <PlayerLink mlbId={player.player_id} name={player.name} style={{ color: '#f9fafb' }} />
                {` (${player.position})`}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      key: 'players_sent',
      title: 'Players Out',
      width: 150,
      sortable: false,
      render: (value, row) => {
        const players = row.players_sent || [];
        if (players.length === 0) return <div className={dynastyTheme.classes.text.neutralLighter}>-</div>;
        return (
          <div className="space-y-1">
            {players.map((player, idx) => (
              <span key={idx} className="text-sm block">
                <PlayerLink mlbId={player.player_id} name={player.name} style={{ color: '#f9fafb' }} />
                {` (${player.position})`}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      key: 'cash',
      title: 'Cash +/-',
      width: 100,
      sortable: true,
      render: (value, row) => {
        // 9k: use ?? (not ||) so explicit 0 from backend is preserved honestly.
        // Render $0.00 instead of "-" so user sees confirmation of zero impact
        // rather than an ambiguous dash (per 8m discipline + Tony's NO FALLBACKS rule).
        const cashSent = row.cash_sent ?? 0;
        const cashReceived = row.cash_received ?? 0;
        const netCash = cashReceived - cashSent;
        const className = netCash > 0
          ? 'text-green-400'
          : netCash < 0
            ? 'text-red-400'
            : dynastyTheme.classes.text.neutralLighter;
        const sign = netCash > 0 ? '+' : netCash < 0 ? '-' : '';
        return (
          <div className={className}>
            {sign}${Math.abs(netCash).toFixed(2)}
          </div>
        );
      }
    },
    {
      key: 'other_team',
      title: 'Other Team',
      width: 150,
      sortable: false,
      render: (value) => (
        <div className={dynastyTheme.classes.text.neutralLight}>
          {value || '-'}
        </div>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 130,
      sortable: false,
      render: (value, row) => {
        // 9k: Actions column now branches on trade / reversed / commissioner per Tony's
        // ERADICATION MODE. Backend endpoint DELETE /transactions/{id} (commissioner-only)
        // already exists; this surfaces it in the UI for non-trade rows.
        if (row.trade_id) {
          return (
            <button
              className={`text-sm ${dynastyTheme.classes.text.primary} hover:underline`}
              onClick={() => handleViewTrade(row.trade_id)}
            >
              View Trade
            </button>
          );
        }
        if (row.is_reversed) {
          return (
            <span
              className="text-xs text-gray-500 italic"
              title={row.reversed_at ? `Reversed ${row.reversed_at}` : 'Reversed'}
            >
              Reversed
            </span>
          );
        }
        if (isCommissioner) {
          return (
            <button
              className={`text-sm ${dynastyTheme.classes.text.error} hover:underline inline-flex items-center gap-1`}
              onClick={() => openReverseConfirm(row.transaction_id, row.description)}
              title="Reverse this transaction (commissioner only)"
            >
              <Undo2 size={12} />
              Reverse
            </button>
          );
        }
        return <div className={dynastyTheme.classes.text.neutralLighter}>-</div>;
      }
    }
  ];

  // Ledger table columns
  const ledgerColumns = [
    {
      key: 'date',
      title: 'Date',
      width: 100,
      sortable: true,
      render: (value) => {
        if (!value) return '-';
        const date = new Date(value);
        return (
          <div className={dynastyTheme.classes.text.neutralLight}>
            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        );
      }
    },
    {
      key: 'transaction_type',
      title: 'Type',
      width: 150,
      sortable: true,
      render: (value) => {
        const typeLabels = {
          'season_start_injection': 'Season Start',
          // B.11.A+B+C labels for per-player IN_SEASON-flip ledger entries
          'season_lock_salary': 'Season Salary Lock',
          'season_lock_dead_money': 'Dead Money Lock',
          'roster_lock': 'Roster Lock',
          // 'keeper_selection' label REMOVED in B.11.A+B+C — no backend writer
          // creates this transaction_type (verified Q-B11-8 audit).
          'keeper_unsubmit': 'Keeper Unsubmit',
          'fa_acquisition': 'FA Add',
          'free_agent': 'FA Claim',
          'commissioner_add': 'Commish Add',
          'draft_pick': 'Drafted',
          'autodraft': 'Autodraft',
          'minor_draft_pick': 'Minor Draft',
          'minor_promotion': 'Promotion',
          'release': 'Release',
          'trade': 'Trade',
          'trade_reversal': 'Trade Reversed',
          'roster_move': 'Move',
          'offseason_reset': 'Offseason Reset'
        };
        return (
          <div className={dynastyTheme.classes.text.white}>
            {typeLabels[value] || value}
          </div>
        );
      }
    },
    {
      key: 'description',
      title: 'Description',
      width: 250,
      sortable: false,
      render: (value) => (
        <div className={dynastyTheme.classes.text.neutralLight}>
          {value || '-'}
        </div>
      )
    },
    {
      key: 'amount',
      title: 'Amount',
      width: 100,
      sortable: true,
      render: (value) => {
        const amount = parseFloat(value || 0);
        return (
          <div className={amount >= 0 ? 'text-green-400' : 'text-red-400'}>
            {amount >= 0 ? '+' : ''}${amount.toFixed(2)}
          </div>
        );
      }
    },
    {
      key: 'balance_after',
      title: 'Balance',
      width: 100,
      sortable: true,
      render: (value) => (
        <div className={dynastyTheme.classes.text.white}>
          ${parseFloat(value || 0).toFixed(2)}
        </div>
      )
    },
    {
      key: 'is_temporary',
      title: 'Status',
      width: 100,
      sortable: true,
      render: (value) => {
        const isTemporary = value === true;
        return (
          <div className="flex items-center gap-2">
            {isTemporary ? (
              <>
                <span className="text-yellow-400">⚠️</span>
                <span className={dynastyTheme.classes.text.warning}>Temporary</span>
              </>
            ) : (
              <>
                <span className="text-green-400">✅</span>
                <span className="text-green-400">Locked</span>
              </>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <TeamPageHeader
        title="Team Transactions"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        badges={false}
        subtitle={
          ledgerData && (
            <span className={`font-semibold ${dynastyTheme.classes.text.primary}`}>
              Balance: ${ledgerData.current_balance?.toFixed(2) || '0.00'}
            </span>
          )
        }
        actions={
          <div className="flex items-center gap-3">
            <label className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight}`}>
              View Team:
            </label>
            <div className="relative">
              <select
                value={selectedTeamId || ''}
                onChange={(e) => handleTeamChange(e.target.value)}
                className={`${dynastyTheme.components.input} px-3 py-1.5 pr-8 min-w-[200px] text-sm appearance-none cursor-pointer`}
              >
                {sortedTeams.map(team => (
                  <option key={team.team_id} value={team.team_id}>
                    {team.team_id === userTeam?.team_id
                      ? `My Team (${team.team_name})`
                      : team.team_name}
                  </option>
                ))}
              </select>
              <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${dynastyTheme.classes.text.neutralLight} pointer-events-none`} />
            </div>
          </div>
        }
      />

      {/* Tabs */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-4">
          <div className={`flex border-b ${dynastyTheme.classes.border.neutral} mb-4`}>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ${
                activeTab === 'transactions'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                  : dynastyTheme.classes.text.neutralLight
              }`}
            >
              All Transactions
            </button>
            <button
              onClick={() => setActiveTab('ledger')}
              className={`px-4 py-2 font-medium ${dynastyTheme.classes.transition} ml-6 ${
                activeTab === 'ledger'
                  ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                  : dynastyTheme.classes.text.neutralLight
              }`}
            >
              Cash Ledger
            </button>
          </div>

          {/* Tab Content */}
          {loading ? (
            <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dynastyTheme.classes.border.primary} mx-auto mb-4`}></div>
              Loading...
            </div>
          ) : error ? (
            <div className={`${dynastyTheme.classes.text.error} text-center py-12`}>
              {error}
            </div>
          ) : activeTab === 'transactions' ? (
            transactions.length === 0 ? (
              <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
                <ArrowRightLeft className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No transactions found</p>
              </div>
            ) : (
              <DynastyTable
                data={transactions}
                columns={transactionColumns}
                tableType="flat-data"
                maxHeight="600px"
                initialSort={{ key: 'date', direction: 'desc' }}
              />
            )
          ) : (
            ledgerData?.ledger_entries?.length === 0 ? (
              <div className={`${dynastyTheme.classes.text.neutralLight} text-center py-12`}>
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No ledger entries found</p>
              </div>
            ) : (
              <DynastyTable
                data={ledgerData?.ledger_entries || []}
                columns={ledgerColumns}
                tableType="flat-data"
                maxHeight="600px"
                initialSort={{ key: 'date', direction: 'desc' }}
              />
            )
          )}
        </div>
      </div>

      {/* Player Profile Modal */}
      <PlayerProfileModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerModalOpen}
        onClose={() => {
          setIsPlayerModalOpen(false);
          setSelectedPlayerId(null);
        }}
        userTeamId={selectedTeamId}
      />

      {/* Trade Detail Modal */}
      {selectedTrade && (
        <TradeDetailModal
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
        />
      )}

      {/* 9k: Reverse Transaction confirmation modal (commissioner-only path) */}
      {pendingReverse && (
        <ReverseConfirmModal
          description={pendingReverse.description}
          inFlight={reverseInFlight}
          onConfirm={handleReverseConfirm}
          onCancel={handleReverseCancel}
        />
      )}
    </div>
  );
};

// TradeAssets Component
const TradeAssets = ({ assets }) => {
  if (!assets) return <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>Nothing</p>;

  const { players = [], picks = [], cash = 0 } = assets;

  return (
    <div className="space-y-2">
      {/* Players */}
      {players.length > 0 && (
        <div>
          <p className={`text-xs ${dynastyTheme.classes.text.neutralLighter} mb-1`}>Players:</p>
          {players.map((player, idx) => (
            <div key={idx} className={`text-sm ${dynastyTheme.classes.text.white}`}>
              • {player.name || `Player ${idx + 1}`}
              {player.position && ` (${player.position})`}
              {player.salary && ` - $${player.salary}`}
            </div>
          ))}
        </div>
      )}

      {/* Draft Picks */}
      {picks.length > 0 && (
        <div>
          <p className={`text-xs ${dynastyTheme.classes.text.neutralLighter} mb-1`}>Draft Picks:</p>
          {picks.map((pick, idx) => (
            <div key={idx} className={`text-sm ${dynastyTheme.classes.text.white}`}>
              • {pick.year} {pick.pick_type === 'ml' ? 'ML ' : ''}Round {pick.round}
              {pick.original_team && ` (${pick.original_team})`}
            </div>
          ))}
        </div>
      )}

      {/* Cash */}
      {cash > 0 && (
        <div>
          <p className={`text-xs ${dynastyTheme.classes.text.neutralLighter} mb-1`}>Cash:</p>
          <div className={`text-sm ${dynastyTheme.classes.text.white}`}>
            ${cash.toFixed(2)}
          </div>
        </div>
      )}

      {/* Empty state */}
      {players.length === 0 && picks.length === 0 && cash === 0 && (
        <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>Nothing</p>
      )}
    </div>
  );
};

// TradeDetailModal Component
const TradeDetailModal = ({ trade, onClose }) => {
  if (!trade) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className={`${dynastyTheme.classes.bg.darkCard} rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className={`p-6 border-b ${dynastyTheme.classes.border.neutral} flex items-center justify-between`}>
          <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
            Trade Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Trade Summary */}
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.primary}`}>
                {trade.proposing_team_name}
              </h3>
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>Proposing Team</p>
            </div>
            <ArrowRightLeft className="w-8 h-8 text-teal-400" />
            <div className="text-center">
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.primary}`}>
                {trade.receiving_team_name}
              </h3>
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>Receiving Team</p>
            </div>
          </div>

          {/* Trade Assets */}
          <div className="grid grid-cols-2 gap-6">
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.neutral}`}>
              <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-3`}>
                {trade.proposing_team_name} Gives:
              </h4>
              <TradeAssets assets={trade.proposing_team_gives} />
            </div>

            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.neutral}`}>
              <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-3`}>
                {trade.receiving_team_name} Gives:
              </h4>
              <TradeAssets assets={trade.receiving_team_gives} />
            </div>
          </div>

          {/* Timeline */}
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
            <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-3`}>Timeline</h4>
            <div className="space-y-2 text-sm">
              {trade.proposed_at && (
                <div className="flex justify-between">
                  <span className={dynastyTheme.classes.text.neutralLighter}>Proposed:</span>
                  <span className={dynastyTheme.classes.text.white}>
                    {new Date(trade.proposed_at).toLocaleString()}
                  </span>
                </div>
              )}
              {trade.responded_at && (
                <div className="flex justify-between">
                  <span className={dynastyTheme.classes.text.neutralLighter}>Responded:</span>
                  <span className={dynastyTheme.classes.text.white}>
                    {new Date(trade.responded_at).toLocaleString()}
                  </span>
                </div>
              )}
              {trade.executed_at && (
                <div className="flex justify-between">
                  <span className={dynastyTheme.classes.text.neutralLighter}>Executed:</span>
                  <span className={dynastyTheme.classes.text.white}>
                    {new Date(trade.executed_at).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className={dynastyTheme.classes.text.neutralLighter}>Status:</span>
                <span className={dynastyTheme.classes.text.white}>
                  {trade.trade_status || 'Unknown'}
                </span>
              </div>
            </div>
          </div>

          {/* Notes & Rejection Reason */}
          {trade.notes && (
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
              <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-2`}>Notes</h4>
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                {trade.notes}
              </p>
            </div>
          )}

          {trade.rejection_reason && (
            <div className={`p-4 rounded-lg bg-red-500/10 border border-red-500/30`}>
              <h4 className="font-bold text-red-400 mb-2">Rejection Reason</h4>
              <p className="text-sm text-red-300">
                {trade.rejection_reason}
              </p>
            </div>
          )}

          {trade.commissioner_notes && (
            <div className={`p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30`}>
              <h4 className="font-bold text-yellow-400 mb-2">Commissioner Notes</h4>
              <p className="text-sm text-yellow-300">
                {trade.commissioner_notes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-6 border-t ${dynastyTheme.classes.border.neutral} flex justify-end`}>
          <button
            onClick={onClose}
            className={dynastyTheme.components.button.secondary}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamTransactions;

